import { useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  patchApiData,
  patchApiFormData,
  postApiData,
} from "../../helpers/AxiosInstance";
import { toast } from "react-toastify";
import { tw } from "@careinfo/tailwind";
import loadable from "@loadable/component";
import useGetHook from "../../customHooks/useGetHook";
import Spinner from "../../components/Reusable/Spinner";
import APIS from "../../constant/Endpoint";
import { usePDFPrintHook } from "@careinfo/layout";
import usePostHook from "../../customHooks/usePostHook";
const EditDebitNoteComponent = loadable(
  () => import("../../components/DebitNote/EditDebitNote")
);

const EditDebitNote = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const { downloadPrint, isPrintLoading } = usePDFPrintHook();
  const [isRevertLoading, setRevertLoading] = useState(false);
  const [openPrintPopup, setOpenPrintPopup] = useState(false);
  const navigate = useNavigate();

  const { id } = useParams();
  const queryClient = useQueryClient();

  const { data, isLoading: isDataLoading } = useGetHook({
    queryKey: ["debit-note", `debit-note-${id}`],
    url: `${APIS.JOURNAL_ENTRY}${id}/`,
    params: {},
  });

  const handleEdit = async (values: any, isDraft: any, setError: any) => {
    setIsLoading(true);
    const url = `${APIS.JOURNAL_ENTRY}${id}/`;
    const formData = new FormData();
    formData.append("voucher_date", values?.voucher_date ?? null);
    formData.append("reference_number", values?.reference_number ?? "");
    values?.reference_date
      ? formData.append("reference_date", values?.reference_date)
      : null;
    formData.append("number_series", values?.number_series);
    formData.append("entry_number", values?.entry_number);
    isDraft
      ? formData.append("status", "Draft")
      : formData.append("status", "Success");
    formData.append("type", "Debit Note");
    if (values?.journal_account) {
      formData.append(
        "journal_account",
        JSON.stringify(values.journal_account)
      );
    }
    if (values?.attachment?.[0] && typeof values?.attachment !== "string") {
      formData.append("attachment", values?.attachment?.[0]);
    }
    values?.narration ? formData.append("narration", values?.narration) : null;
    try {
      const res = await patchApiFormData({ url, formData, setError });
      if (res?.status === 200) {
        queryClient.invalidateQueries({
          queryKey: ["debit-note"],
          refetchType: "active",
        });
        if (!isDraft) {
          setSelectedOrder(res?.data?.id);
          setOpenPrintPopup(true);
        } else {
          navigate("/accounting/debit-note");
        }
        toast.success("Updated Successfully");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = async () => {
    const url = APIS.BILL_PRINT;
    let formData = {
      ids: [selectedOrder],
      type: "Debit Note",
    };

    try {
      const res = await downloadPrint(url, formData);
    } catch (err) {
      console.error(err);
    }
  };
  const handleCancel = async () => {
    const url = `${APIS.JOURNAL_ENTRY}${id}/`;
    const formData = {
          status: "Cancelled",
    };
    try {
      patchApiData({ url, formData, setError: () => {} });
    } catch (err) {
      console.error(err);
    }
  };
  const handleRevert = async (setError) => {
    const url = `${APIS.BILL}${id}/revert/`;
    setRevertLoading(true);
    try {
      const res = await postApiData({ url, formData: {}, setError });
      if (res?.status === 200) {
        queryClient.invalidateQueries({
          queryKey: ["debit-note"],
          refetchType: "active",
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setRevertLoading(false);
    }
  };
  const { mutateAsync, isPostLoading } = usePostHook({
    queryKey: ["ledger-account"],
    navigateURL: "",
  });

  const handleCreateAccount = async (
    values: any,
    setOpenAccountPopup: any,
    setError: any
  ) => {
    const url = APIS.COA;
    const formData = {
      ...values,

      is_group: false,
    };
    try {
      const res = await mutateAsync({ url, formData, setError });
      if (res?.status === 201) {
        setOpenAccountPopup(false);
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div>
      <div className={tw`grid grid-cols-2 `}>
        <h1 className={tw`font-semibold text-xl leading-10`}>Debit Note</h1>
      </div>
      {isDataLoading ? (
        <Spinner />
      ) : (
        <EditDebitNoteComponent
          handleEdit={handleEdit}
          isLoading={isLoading}
          data={data}
          openPrintPopup={openPrintPopup}
          setOpenPrintPopup={setOpenPrintPopup}
          handlePrint={handlePrint}
          handleRevert={handleRevert}
          isRevertLoading={isRevertLoading}
          isPrintLoading={isPrintLoading}
          handleCancel={handleCancel}
          handleCreateAccount={handleCreateAccount}
          isPostLoading={isPostLoading}
        />
      )}
    </div>
  );
};

export default EditDebitNote;
